import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Derives traffic source from referrer URL and query params.
 */
function getSource(referrer: string, searchParams: URLSearchParams): string {
  // Check UTM / ref params first
  const utm = searchParams.get("utm_source")?.toLowerCase() || searchParams.get("ref")?.toLowerCase();
  if (utm) {
    if (utm.includes("google")) return "google";
    if (utm.includes("facebook") || utm.includes("fb")) return "facebook";
    if (utm.includes("instagram") || utm.includes("ig")) return "instagram";
    if (utm.includes("bing")) return "bing";
    return "other";
  }

  if (!referrer) return "direct";

  try {
    const host = new URL(referrer).hostname;
    if (host === window.location.hostname) return "direct";
    if (host.includes("google.")) return "google";
    if (host.includes("facebook.") || host.includes("fb.")) return "facebook";
    if (host.includes("instagram.")) return "instagram";
    if (host.includes("bing.")) return "bing";
    return "other";
  } catch {
    return "other";
  }
}

/**
 * Generates a daily anonymous hash for unique visitor counting.
 * Combines date + screen resolution + timezone + language.
 * Rotates daily, no PII, no cookies.
 */
async function generateVisitorHash(): Promise<string> {
  const date = new Date().toISOString().split("T")[0];
  const screenRes = `${screen.width}x${screen.height}`;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const lang = navigator.language;
  const raw = `${date}-${screenRes}-${tz}-${lang}`;

  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(raw));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function PageViewTracker() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname;

    // Skip admin pages
    if (path.startsWith("/admin")) return;

    // Skip duplicate fires for same path
    if (lastPath.current === path) return;
    lastPath.current = path;

    (async () => {
      try {
        const visitorHash = await generateVisitorHash();
        const source = getSource(document.referrer, new URLSearchParams(location.search));

        await supabase.from("page_views" as any).insert({
          path,
          referrer: document.referrer || null,
          source,
          visitor_hash: visitorHash,
          user_agent: navigator.userAgent,
        } as any);
      } catch {
        // Fail silently - never interrupt user experience
      }
    })();
  }, [location.pathname, location.search]);

  return null;
}
